import type { LucideIcon } from 'lucide-react';
import { CircleDollarSign, Home, Inbox, UserRound } from 'lucide-react';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleBoundaryError } from '@/utility/reportRenderError';
import WaveLogo from '@/creator/components/WaveLogo';
import HelpMenu from '@/creator/components/HelpMenu';
import '@/creator-app.css';

/**
 * App shell for the reworked logged-in creator area (ROU-1192), ported from the
 * prototype's AppShell and wired to react-router. Replaces WaveLayout for the
 * creator routes. Nav is intentionally the four prototype destinations (home ·
 * campaigns · wallet · account); the legacy invitations/posts routes stay live
 * but are no longer linked from the shell.
 *
 * Buttons (not NavLinks) are kept so the ported `.top-nav`/`.bottom-nav` button
 * styles apply unchanged.
 */

type NavItem = {
    to: string;
    label: string;
    Icon: LucideIcon;
};

const NAV_ITEMS: NavItem[] = [
    { to: '/', label: 'Home', Icon: Home },
    { to: '/campaigns', label: 'Campaigns', Icon: Inbox },
    { to: '/wallet', label: 'Wallet', Icon: CircleDollarSign },
    { to: '/account', label: 'Account', Icon: UserRound },
];

const isNavItemActive = (to: string, pathname: string): boolean => {
    if (to === '/') {
        return pathname === '/';
    }
    return pathname === to || pathname.startsWith(`${to}/`);
};

const AppShell = ({ children }: PropsWithChildren) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Reset scroll on navigation, mirroring the prototype's per-page scroll reset.
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    return (
        <div className="app-shell">
            <header className="top-nav">
                <WaveLogo />
                <nav aria-label="Primary navigation">
                    {NAV_ITEMS.map(({ to, label, Icon }) => {
                        const isActive = isNavItemActive(to, location.pathname);
                        return (
                            <button
                                type="button"
                                key={to}
                                className={isActive ? 'is-active' : ''}
                                aria-current={isActive ? 'page' : undefined}
                                onClick={() => navigate(to)}
                            >
                                <Icon size={18} />
                                <span>{label}</span>
                            </button>
                        );
                    })}
                    <span className="top-nav__separator" aria-hidden="true" />
                    <HelpMenu />
                </nav>
            </header>

            <main className="main-panel">
                <header className="mobile-header">
                    <WaveLogo compact />
                    <HelpMenu />
                </header>
                <ErrorBoundary
                    onError={handleBoundaryError}
                    fallbackRender={() => <p className="page">Something went wrong.</p>}
                >
                    {children}
                </ErrorBoundary>
            </main>

            <nav className="bottom-nav" aria-label="Mobile navigation">
                {NAV_ITEMS.map(({ to, label, Icon }) => {
                    const isActive = isNavItemActive(to, location.pathname);
                    return (
                        <button
                            type="button"
                            key={to}
                            className={isActive ? 'is-active' : ''}
                            aria-current={isActive ? 'page' : undefined}
                            onClick={() => navigate(to)}
                        >
                            <Icon size={19} />
                            <span>{label}</span>
                        </button>
                    );
                })}
            </nav>
        </div>
    );
};

export default AppShell;
